/**
 * Hook del detalle de un Aliado (US-2.2). La red vive en `allyApi`; acá solo el
 * estado de la vista (loading / no encontrado / error / refresh), separable de la
 * presentación — mismo patrón que `useAllies`.
 */
import { useCallback, useEffect, useState } from 'react';
import type { Ally } from '@goelectric/shared-types';
import { ApiError } from '@/lib/http/apiClient';
import { allyApi } from '../api/allyApi';

export interface UseAllyResult {
  ally: Ally | null;
  loading: boolean;
  error: string | null;
  /** `true` cuando el backend responde 404 (aliado inexistente). */
  notFound: boolean;
  refresh: () => void;
}

function toErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return 'No se pudo cargar el aliado.';
}

export function useAlly(allyId: string | undefined): UseAllyResult {
  const [ally, setAlly] = useState<Ally | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!allyId) {
      setLoading(false);
      setNotFound(true);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setNotFound(false);

    allyApi
      .get(allyId)
      .then((result) => {
        if (!cancelled) setAlly(result);
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        if (caught instanceof ApiError && caught.status === 404) {
          setNotFound(true);
        } else {
          setError(toErrorMessage(caught));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [allyId, reloadKey]);

  const refresh = useCallback(() => setReloadKey((key) => key + 1), []);

  return { ally, loading, error, notFound, refresh };
}
